import { QuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { DropdownOption, QuestionnaireData } from "@teamprevent/common/questionnaires/model";

import { mhpConsultationMasterConfig } from "./mhpTreatmentConsultationMasterConfig";

export interface CreateMHPCaseTreatmentModel {
    customerId: string;
    employeeRecordId: string;
    initialTaskType: string;
    linkedOhCaseId?: string;
}

export const MHP_TREATMENT_CONSULTATION = "MHP_TREATMENT_CONSULTATION";

export const ConsultationSectionIds = {
    ConsultationDetails: "ConsultationDetails",
    CompletedOnBehalfOf: "CompletedOnBehalfOf",
    IntroductionAndDataPrivacy: "IntroductionAndDataPrivacy",
    QuestionnairesAndRisk: "QuestionnairesAndRisk",
    IaptQuestionnaire: "IaptQuestionnaire",
    ClinicalNotes: "ClinicalNotes",
    StandardClinicalDetailsAndOutcome: "StandardClinicalDetailsAndOutcome",
    ConsentAndReport: "ConsentAndReport",
    NextTasks: "NextTasks"
};

export const getQuestionConfig = (questionId: string): QuestionConfig => {
    return mhpConsultationMasterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
};

export const ConfirmedAnswer = "Confirmed";
export const ConsentedAnswer = "Consented";

export enum RiskLetterToGP {
    Yes = "Yes",
    No = "No"
}

export const riskLetterToGPOptions: DropdownOption[] = [
    { text: "Yes", value: RiskLetterToGP.Yes },
    { text: "No", value: RiskLetterToGP.No }
];

export enum CurrentWorkStatus {
    InWorkFullDuties = "InWorkFullDuties",
    InWOrkRestrictions = "InWOrkRestrictions",
    OffWork = "OffWork",
    Suspended = "Suspended",
    OnPhasedReturn = "OnPhasedReturn"
}

export const currentWorkStatusOptions: DropdownOption[] = [
    { text: "In Work (Full Duties)", value: CurrentWorkStatus.InWorkFullDuties },
    { text: "In Work (Restrictions)", value: CurrentWorkStatus.InWOrkRestrictions },
    { text: "Off Work", value: CurrentWorkStatus.OffWork },
    { text: "Suspended", value: CurrentWorkStatus.Suspended },
    { text: "On Phased Return", value: CurrentWorkStatus.OnPhasedReturn }
];

export enum RiskAssessmentCompleted {
    Yes = "Yes",
    No = "No"
}

export const riskAssessmentCompletedOptions: DropdownOption[] = [
    { text: "Yes", value: RiskAssessmentCompleted.Yes },
    { text: "No", value: RiskAssessmentCompleted.No }
];

export enum ProvisionalDiagnosis {
    GeneralisedAnxietyDisorder = "GeneralisedAnxietyDisorder",
    Bereavement = "Bereavement",
    Depression = "Depression",
    MentalIllnessSevere = "MentalIllnessSevere",
    StressWorkRelated = "StressWorkRelated",
    StressPersonalLife = "StressPersonalLife",
    StressMixedWorkAndPersonalLife = "StressMixedWorkAndPersonalLife",
    PTSD = "PTSD",
    SocialAnxiety = "SocialAnxiety",
    SpecificPhobia = "SpecificPhobia",
    Agoraphobia = "Agoraphobia",
    PanicDisorder = "PanicDisorder",
    MixedAnxietyAndDepression = "MixedAnxietyAndDepression",
    OCD = "OCD"
}

export const provisionalDiagnosisOptions: DropdownOption[] = [
    { text: "Generalised Anxiety Disorder", value: ProvisionalDiagnosis.GeneralisedAnxietyDisorder },
    { text: "Bereavement", value: ProvisionalDiagnosis.Bereavement },
    { text: "Depression", value: ProvisionalDiagnosis.Depression },
    { text: "Mental Illness (Severe)", value: ProvisionalDiagnosis.MentalIllnessSevere },
    { text: "Stress (Work related)", value: ProvisionalDiagnosis.StressWorkRelated },
    { text: "Stress (Personal Life)", value: ProvisionalDiagnosis.StressPersonalLife },
    { text: "Stress (Mixed work and personal life)", value: ProvisionalDiagnosis.StressMixedWorkAndPersonalLife },
    { text: "PTSD", value: ProvisionalDiagnosis.PTSD },
    { text: "Social Anxiety", value: ProvisionalDiagnosis.SocialAnxiety },
    { text: "Specific Phobia", value: ProvisionalDiagnosis.SpecificPhobia },
    { text: "Agoraphobia", value: ProvisionalDiagnosis.Agoraphobia },
    { text: "Panic Disorder ", value: ProvisionalDiagnosis.PanicDisorder },
    { text: "Mixed Anxiety and Depression", value: ProvisionalDiagnosis.MixedAnxietyAndDepression },
    { text: "OCD", value: ProvisionalDiagnosis.OCD }
];

export const coMorbidConditionsOptions = {
    Autism: "Autism",
    ADHD: "ADHD",
    ADD: "ADD",
    MUS: "MUS",
    IBS: "IBS",
    PersonalityDisorder: "Personality Disorder",
    Schizophrenia: "Schizophrenia",
    Psychosis: "Psychosis",
    Other: "Other"
};

export const treatmentCompletedInTodaysSessionOptions = {
    BA: "BA",
    CR: "CR",
    WM: "WM",
    Relaxation: "Relaxation",
    Mindfulness: "Mindfulness",
    StressManagement: "Stress Management",
    AngerManagement: "Anger Management",
    Other: "Other"
};

export const sessionsSignpostingDischargeOptions = {
    BookMHPConsultation: "Book MHP consultation",
    LastSessionGenerateDischargeReport: "Last session generate discharge report",
    SignpostToOtherServices: "Signpost to other services"
};

export const signpostToOtherServicesOptions = {
    Counselling: "Counselling",
    EAP: "EAP",
    IAPTServices: "IAPT services",
    CareOfGP: "Care of GP",
    Other: "Other"
};

export enum OutcomeFinalSessionOnly {
    Recovered = "Recovered",
    Worse = "Worse",
    NoChange = "NoChange",
    DNA = "DNA",
    ImprovedButNotInClinicalRecovery = "ImprovedButNotInClinicalRecovery"
}

export const outcomeFinalSessionOnlyOptions: DropdownOption[] = [
    { text: "Recovered", value: OutcomeFinalSessionOnly.Recovered },
    { text: "Worse", value: OutcomeFinalSessionOnly.Worse },
    { text: "No Change", value: OutcomeFinalSessionOnly.NoChange },
    { text: "DNA", value: OutcomeFinalSessionOnly.DNA },
    { text: "Improved but not in clinical recovery", value: OutcomeFinalSessionOnly.ImprovedButNotInClinicalRecovery }
];

export enum ConsentAndReport {
    Yes = "Yes",
    No = "No"
}

export const consentAndReportOptions: DropdownOption[] = [
    { text: "Yes", value: ConsentAndReport.Yes },
    { text: "No", value: ConsentAndReport.No }
];

export enum ConsentToReleaseTheReportAnswers {
    CopyRequired = "CopyRequired",
    CopyRequiredSameTime = "CopyRequiredSameTime",
    AlternativeScope = "AlternativeScope"
}

export interface MHPTreatmentConsultation {
    id: string;
    data: QuestionnaireData;
    eTag: string;
}
